// src/pages/DeepTechHome.jsx
import React from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBrain,
  faMicrochip,
  faDatabase,
  faNetworkWired,
  faShieldAlt,
  faEye,
  faRocket,
  faGlobe
} from "@fortawesome/free-solid-svg-icons";

const DeepTechHome = () => {
  const existingFeatures = [
    {
      icon: faBrain,
      color: "text-cyan-300",
      title: "AI & Cognitive Intelligence",
      desc: "Context-aware automation, predictive analytics, and decision intelligence integrated into business processes."
    },
    {
      icon: faMicrochip,
      color: "text-blue-300",
      title: "IoT & Digital Twin Framework",
      desc: "Real-time physical–digital synchronization for factories, cities, and supply chains."
    },
    {
      icon: faDatabase,
      color: "text-purple-300",
      title: "Data Science Engine",
      desc: "Unified data fabric connecting edge-to-cloud pipelines for insight generation."
    },
    {
      icon: faNetworkWired,
      color: "text-green-300",
      title: "Web3 Integration Layer",
      desc: "Smart contracts, tokenization, and decentralized identity enable transparent and trusted ecosystems."
    },
  ];

  const roadmapFeatures = [
    {
      icon: faShieldAlt,
      color: "text-teal-300",
      title: "Quantum & Edge Security",
      desc: "Quantum-resistant encryption and low-latency edge computing infrastructure for mission-critical applications."
    },
    {
      icon: faEye,
      color: "text-pink-300",
      title: "Metaverse / XR Interface",
      desc: "Immersive digital twin visualization and holographic workspaces for next-gen collaboration."
    },
    {
      icon: faRocket,
      color: "text-orange-300",
      title: "Autonomous DAO Ecosystem",
      desc: "Self-governing digital ecosystems enabling community-led innovation and resource sharing."
    },
    {
      icon: faGlobe,
      color: "text-indigo-300",
      title: "Sustainability & ESG Intelligence",
      desc: "AI-driven traceability, carbon credit tokenization, and responsible transformation metrics."
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#030014] to-[#0a043c] text-white flex flex-col items-center justify-center px-6 py-8">
      <motion.h1
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="text-xl md:text-2xl font-bold mb-4 text-center"
      >
        Think5Whys Global DeepTech Ecosystem
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 1 }}
        className="text-sm md:text-m text-gray-300 text-center max-w-3xl mb-4"
      >
        Bridging intelligence and trust for Viksit, Atmanirbhar and SAKSHAM Bharat — transforming enterprises, MSMEs and communities 
        through an intelligent, decentralized and sustainable digital evolution. 
      </motion.p>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 1 }}
        className="text-sm md:text-m text-purple-300 text-center max-w-3xl mb-4"
      >
        Building Transformation-as-a-Platform. Made In India. Made for India. Made for the World.
      </motion.p>

      {/* EXISTING FEATURES */}
      <motion.h2
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-xl font-semibold mb-4 text-cyan-300"
      >
        ⚙️ Existing Capabilities
      </motion.h2>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {existingFeatures.map((f, i) => (
          <motion.div
            key={i}
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="bg-[#0f0a2a]/60 border border-cyan-700/30 backdrop-blur-xl shadow-lg rounded-2xl p-6 flex flex-col items-center text-center space-y-3 hover:shadow-cyan-500/40 transition-shadow"
          >
            <FontAwesomeIcon icon={f.icon} className={`w-10 h-10 ${f.color}`} />
            <h3 className="text-lg font-semibold">{f.title}</h3>
            <p className="text-gray-400 text-xs">{f.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* ROADMAP */}
      <motion.h2
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="text-xl font-semibold mb-4 text-pink-300"
      >
        🚀 Roadmap Vision
      </motion.h2>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
        {roadmapFeatures.map((f, i) => (
          <motion.div
            key={i}
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="bg-[#0a0520]/60 border border-pink-700/30 backdrop-blur-xl shadow-lg rounded-2xl p-6 flex flex-col items-center text-center space-y-3 hover:shadow-pink-500/40 transition-shadow"
          >
            <FontAwesomeIcon icon={f.icon} className={`w-10 h-10 ${f.color}`} />
            <h3 className="text-lg font-semibold">{f.title}</h3>
            <p className="text-gray-400 text-xs">{f.desc}</p>
          </motion.div>
        ))}
      </div>

      <motion.button
        whileHover={{ scale: 1.05 }}
        className="border border-cyan-400 text-cyan-300 px-6 py-2 rounded-lg hover:bg-cyan-500/20 transition-all font-semibold mb-10"
      >
        Explore Transformation-as-a-Platform →
      </motion.button>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="text-xs text-gray-500 mt-4"
      >
        © {new Date().getFullYear()} By: Think5Whys Global DeepTech Solutions, Mumbai
      </motion.p>
    </div>
  );
};

export default DeepTechHome;
